// Sección de listado de propiedades
// Incluye: título, lista de PropertyCards, estados vacíos, loading

import React from 'react';
import { Archive, Eye } from 'lucide-react';
import PropertyCard from '../PropertyCard';
import EmptyState from './EmptyState';

/**
 * Sección de propiedades del AdminDashboard
 * Muestra la lista de propiedades (activas o archivadas) con sus estados
 * 
 * @param {array} properties - Lista de propiedades a mostrar
 * @param {boolean} showArchived - Si está en modo archivadas
 * @param {boolean} isLoading - Si está cargando
 * @param {function} getPropertyBids - Obtener bids de una propiedad
 * @param {function} getPropertyInvites - Obtener invites de una propiedad
 * @param {function} onEdit - Handler para editar propiedad
 * @param {function} onArchive - Handler para archivar propiedad
 * @param {function} onUnarchive - Handler para reactivar propiedad
 * @param {function} onManageInvites - Handler para gestionar invitados
 * @param {function} onViewConditions - Handler para ver condiciones de oferta
 * @param {function} onCloseLastChance - Handler para cerrar última oportunidad
 * @param {function} onToggleArchived - Toggle entre activas/archivadas
 * @param {function} onCreateProperty - Abrir modal de crear propiedad
 */
const PropertiesSection = ({
  properties,
  showArchived,
  isLoading,
  getPropertyBids,
  getPropertyInvites,
  onEdit,
  onArchive,
  onUnarchive,
  onManageInvites,
  onViewConditions,
  onCloseLastChance,
  onStartLastChance,
  onStartBlindBidding,
  onActivateBlindActive,
  onCloseBlindBidding,
  onStartOpenBidding,
  onToggleArchived,
  onCreateProperty
}) => {
  // Loading state
  if (isLoading && showArchived) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando propiedades archivadas...</p>
        </div>
      </div>
    );
  }

  // Empty state - Archivadas
  if (!isLoading && properties.length === 0 && showArchived) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-12 border border-slate-700/50 text-center">
        <Archive className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">No hay propiedades archivadas</h3>
        <p className="text-slate-400 mb-6">Las propiedades que archives aparecerán aquí</p>
        <button
          onClick={onToggleArchived}
          className="inline-flex items-center gap-2 bg-slate-700 text-white font-medium px-6 py-3 rounded-lg hover:bg-slate-600 transition-all"
        >
          <Eye className="w-5 h-5" />
          Ver Activas
        </button>
      </div>
    );
  }

  // Empty state - Activas
  if (!isLoading && properties.length === 0 && !showArchived) {
    return <EmptyState onCreateProperty={onCreateProperty} />;
  }

  // Lista de propiedades
  return (
    <>
      {/* Título de sección */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">
          {showArchived ? 'Propiedades Archivadas' : 'Propiedades'}
        </h2>
      </div>

      {/* Grid de propiedades */}
      <div className="grid grid-cols-1 gap-6">
        {properties.map(property => (
          <PropertyCard
            key={property.id}
            property={property}
            propertyBids={getPropertyBids(property.id)}
            propertyInvites={getPropertyInvites(property.id)}
            onEdit={showArchived ? null : onEdit}
            onArchive={showArchived ? null : onArchive}
            onUnarchive={showArchived ? onUnarchive : null}
            onManageInvites={showArchived ? null : onManageInvites}
            onViewConditions={onViewConditions}
            onCloseLastChance={showArchived ? null : onCloseLastChance}
            onStartLastChance={showArchived ? null : onStartLastChance}
            onStartBlindBidding={showArchived ? null : onStartBlindBidding}
            onActivateBlindActive={showArchived ? null : onActivateBlindActive}
            onCloseBlindBidding={showArchived ? null : onCloseBlindBidding}
            onStartOpenBidding={showArchived ? null : onStartOpenBidding}
            isArchived={showArchived}
          />
        ))}
      </div>
    </>
  );
};

export default PropertiesSection;